import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {
    Search as SearchIcon,
    ViewColumn as ViewColumnIcon,
    FilterList as FilterListIcon,
    DensityMedium as DensityMediumIcon,
    SwapVert as SwapVertIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    Clear as ClearIcon
} from '@mui/icons-material';

const STORAGE_PREFIX = 'advanced_table_prefs_v1';

const getCellComparableValue = (column, row) => {
    if (column.sortAccessor) return column.sortAccessor(row);
    if (column.accessor) return column.accessor(row);
    const raw = row?.[column.id];
    if (raw === null || raw === undefined) return '';
    return raw;
};

const getCellFilterValue = (column, row) => {
    if (column.filterAccessor) return column.filterAccessor(row);
    return getCellComparableValue(column, row);
};

const toComparable = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'boolean') return value ? 1 : 0;
    const numeric = Number(value);
    if (!Number.isNaN(numeric) && `${value}`.trim() !== '') return numeric;
    return `${value}`.toLowerCase();
};

const safeReadPrefs = (storageKey) => {
    if (!storageKey) return null;
    try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}:${storageKey}`);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const savePrefs = (storageKey, prefs) => {
    if (!storageKey) return;
    try {
        localStorage.setItem(`${STORAGE_PREFIX}:${storageKey}`, JSON.stringify(prefs));
    } catch {
        // no-op for private mode/quota issues
    }
};

const AdvancedDataTable = ({
    storageKey,
    rows = [],
    columns = [],
    rowKey = 'id',
    loading = false,
    initialSortBy,
    initialSortOrder = 'asc',
    pageSizeOptions = [10, 25, 50, 100],
    defaultRowsPerPage = 25,
    emptyTitle = 'Sin resultados',
    emptyDescription = 'No hay datos para mostrar con los filtros actuales.',
    onRowClick,
    rowActions,
    toolbarActions,
    compactByDefault = false
}) => {
  const { t } = useTranslation();
    const saved = useMemo(() => safeReadPrefs(storageKey), [storageKey]);

    const defaultColumnIds = useMemo(() => columns.map((c) => c.id), [columns]);

    const [search, setSearch] = useState(saved?.search ?? '');
    const [sortBy, setSortBy] = useState(saved?.sortBy ?? initialSortBy ?? defaultColumnIds[0]);
    const [sortOrder, setSortOrder] = useState(saved?.sortOrder ?? initialSortOrder);
    const [filters, setFilters] = useState(saved?.filters ?? {});
    const [page, setPage] = useState(saved?.page ?? 0);
    const [rowsPerPage, setRowsPerPage] = useState(saved?.rowsPerPage ?? defaultRowsPerPage);
    const [visibleColumns, setVisibleColumns] = useState(saved?.visibleColumns ?? defaultColumnIds);
    const [columnOrder, setColumnOrder] = useState(saved?.columnOrder ?? defaultColumnIds);
    const [dense, setDense] = useState(saved?.dense ?? compactByDefault);

    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [filtersAnchor, setFiltersAnchor] = useState(null);
    const [orderAnchor, setOrderAnchor] = useState(null);

    useEffect(() => {
        const normalizedVisible = visibleColumns.filter((id) => defaultColumnIds.includes(id));
        const normalizedOrder = [
            ...columnOrder.filter((id) => defaultColumnIds.includes(id)),
            ...defaultColumnIds.filter((id) => !columnOrder.includes(id))
        ];

        if (normalizedVisible.length !== visibleColumns.length) {
            setVisibleColumns(normalizedVisible.length ? normalizedVisible : defaultColumnIds);
        }

        if (normalizedOrder.join('|') !== columnOrder.join('|')) {
            setColumnOrder(normalizedOrder);
        }
    }, [defaultColumnIds, visibleColumns, columnOrder]);

    useEffect(() => {
        savePrefs(storageKey, {
            search,
            sortBy,
            sortOrder,
            filters,
            page,
            rowsPerPage,
            visibleColumns,
            columnOrder,
            dense
        });
    }, [storageKey, search, sortBy, sortOrder, filters, page, rowsPerPage, visibleColumns, columnOrder, dense]);

    const columnsById = useMemo(() => {
        const map = {};
        columns.forEach((column) => {
            map[column.id] = column;
        });
        return map;
    }, [columns]);

    const orderedVisibleColumns = useMemo(
        () => columnOrder.filter((id) => visibleColumns.includes(id)).map((id) => columnsById[id]).filter(Boolean),
        [columnOrder, visibleColumns, columnsById]
    );

    const activeFilterEntries = useMemo(
        () => Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && `${value}` !== ''),
        [filters]
    );

    const filteredRows = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        const filterableColumns = columns.filter((column) => column.filterable);

        return rows.filter((row) => {
            if (normalizedSearch) {
                const rowText = columns
                    .map((column) => {
                        const value = getCellFilterValue(column, row);
                        return value === null || value === undefined ? '' : `${value}`;
                    })
                    .join(' ')
                    .toLowerCase();

                if (!rowText.includes(normalizedSearch)) {
                    return false;
                }
            }

            for (const column of filterableColumns) {
                const filterValue = filters[column.id];
                if (filterValue === undefined || filterValue === null || `${filterValue}` === '') continue;

                const cellValue = getCellFilterValue(column, row);
                const normalizedCellValue = cellValue === null || cellValue === undefined ? '' : `${cellValue}`.toLowerCase();
                const normalizedFilterValue = `${filterValue}`.toLowerCase();

                if (column.filterType === 'select') {
                    if (normalizedCellValue !== normalizedFilterValue) return false;
                } else {
                    if (!normalizedCellValue.includes(normalizedFilterValue)) return false;
                }
            }

            return true;
        });
    }, [rows, columns, filters, search]);

    const sortedRows = useMemo(() => {
        if (!sortBy || !columnsById[sortBy]) return filteredRows;
        const column = columnsById[sortBy];

        return [...filteredRows].sort((a, b) => {
            const aValue = toComparable(getCellComparableValue(column, a));
            const bValue = toComparable(getCellComparableValue(column, b));

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredRows, sortBy, sortOrder, columnsById]);

    const pagedRows = useMemo(
        () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedRows, page, rowsPerPage]
    );

    useEffect(() => {
        if (page > 0 && page * rowsPerPage >= sortedRows.length) {
            setPage(0);
        }
    }, [page, rowsPerPage, sortedRows.length]);

    const handleSort = (columnId) => {
        if (sortBy === columnId) {
            setSortOrder((prev) => (prev === 'asc' ? t('hardcoded.components_common_advanceddatatable.desc') : t('hardcoded.components_common_advanceddatatable.asc')));
        } else {
            setSortBy(columnId);
            setSortOrder(t('hardcoded.components_common_advanceddatatable.asc'));
        }
    };

    const clearAllFilters = () => {
        setSearch('');
        setFilters({});
        setPage(0);
    };

    const moveColumn = (columnId, direction) => {
        setColumnOrder((prev) => {
            const idx = prev.indexOf(columnId);
            if (idx < 0) return prev;
            const nextIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (nextIdx < 0 || nextIdx >= prev.length) return prev;
            const clone = [...prev];
            [clone[idx], clone[nextIdx]] = [clone[nextIdx], clone[idx]];
            return clone;
        });
    };

    return (
        <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <TextField
                        size="small"
                        placeholder={t('hardcoded.components_common_advanceddatatable.buscar_en_tabla')}
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                            setPage(0);
                        }}
                        sx={{ minWidth: { xs: '100%', md: 300 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Tooltip title={t('hardcoded.components_common_advanceddatatable.filtros_por_columna')}>
                            <IconButton size="small" onClick={(event) => setFiltersAnchor(event.currentTarget)}>
                                <FilterListIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t('hardcoded.components_common_advanceddatatable.mostrarocultar_columnas')}>
                            <IconButton size="small" onClick={(event) => setColumnsAnchor(event.currentTarget)}>
                                <ViewColumnIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t('hardcoded.components_common_advanceddatatable.orden_de_columnas')}>
                            <IconButton size="small" onClick={(event) => setOrderAnchor(event.currentTarget)}>
                                <SwapVertIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t('hardcoded.components_common_advanceddatatable.densidad_compacta')}>
                            <Switch
                                size="small"
                                checked={dense}
                                onChange={(event) => setDense(event.target.checked)}
                                icon={<DensityMediumIcon fontSize="small" />}
                                checkedIcon={<DensityMediumIcon fontSize="small" />}
                            />
                        </Tooltip>

                        <Button size="small" startIcon={<ClearIcon />} onClick={clearAllFilters}>
                            {i18n.t('common.clear')}</Button>

                        {toolbarActions}
                    </Stack>
                </Stack>

                {activeFilterEntries.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                        {activeFilterEntries.map(([columnId, value]) => (
                            <Chip
                                key={columnId}
                                size="small"
                                label={`${columnsById[columnId]?.label || columnId}: ${value}`}
                                onDelete={() => {
                                    setFilters((prev) => ({ ...prev, [columnId]: '' }));
                                    setPage(0);
                                }}
                            />
                        ))}
                    </Stack>
                )}
            </Box>

            <TableContainer>
                <Table size={dense ? 'small' : 'medium'}>
                    <TableHead>
                        <TableRow>
                            {orderedVisibleColumns.map((column) => (
                                <TableCell key={column.id} align={column.align || 'left'}>
                                    {column.sortable === false ? (
                                        column.label
                                    ) : (
                                        <TableSortLabel
                                            active={sortBy === column.id}
                                            direction={sortBy === column.id ? sortOrder : 'asc'}
                                            onClick={() => handleSort(column.id)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    )}
                                </TableCell>
                            ))}
                            {rowActions ? <TableCell align="right">{t('common.actions')}</TableCell> : null}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {!loading && pagedRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={orderedVisibleColumns.length + (rowActions ? 1 : 0)}>
                                    <Alert severity="info" sx={{ my: 1 }}>
                                        <Typography variant="subtitle2">{emptyTitle}</Typography>
                                        <Typography variant="body2">{emptyDescription}</Typography>
                                    </Alert>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pagedRows.map((row) => (
                                <TableRow
                                    key={row[rowKey] || JSON.stringify(row)}
                                    hover
                                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                                    sx={onRowClick ? { cursor: 'pointer' } : undefined}
                                >
                                    {orderedVisibleColumns.map((column) => (
                                        <TableCell key={column.id} align={column.align || 'left'}>
                                            {column.render ? column.render(row) : `${getCellComparableValue(column, row) ?? ''}`}
                                        </TableCell>
                                    ))}
                                    {rowActions ? <TableCell align="right">{rowActions(row)}</TableCell> : null}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={sortedRows.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={pageSizeOptions}
            />

            <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)}>
                {columnOrder.map((columnId) => {
                    const column = columnsById[columnId];
                    if (!column) return null;
                    const checked = visibleColumns.includes(columnId);
                    return (
                        <MenuItem
                            key={columnId}
                            onClick={() => {
                                setVisibleColumns((prev) => {
                                    if (checked) {
                                        const next = prev.filter((id) => id !== columnId);
                                        return next.length ? next : prev;
                                    }
                                    return [...prev, columnId];
                                });
                            }}
                        >
                            <Box sx={{ width: 18, mr: 1 }}>{checked ? '✓' : ''}</Box>
                            {column.label}
                        </MenuItem>
                    );
                })}
            </Menu>

            <Menu anchorEl={filtersAnchor} open={Boolean(filtersAnchor)} onClose={() => setFiltersAnchor(null)}>
                <Box sx={{ p: 2, width: 320 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('hardcoded.components_common_advanceddatatable.filtros_por_columna')}</Typography>
                    <Stack spacing={1.5}>
                        {columns.filter((column) => column.filterable).map((column) => {
                            if (column.filterType === 'select') {
                                return (
                                    <FormControl key={column.id} size="small" fullWidth>
                                        <InputLabel>{column.label}</InputLabel>
                                        <Select
                                            value={filters[column.id] ?? ''}
                                            onChange={(event) => {
                                                setFilters((prev) => ({ ...prev, [column.id]: event.target.value }));
                                                setPage(0);
                                            }}
                                            input={<OutlinedInput label={column.label} />}
                                        >
                                            <MenuItem value="">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                                            {(column.filterOptions || []).map((option) => {
                                                const value = typeof option === 'string' ? option : option.value;
                                                const label = typeof option === 'string' ? option : option.label;
                                                return (
                                                    <MenuItem key={value} value={value}>
                                                        {label}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                );
                            }

                            return (
                                <TextField
                                    key={column.id}
                                    size="small"
                                    label={column.label}
                                    value={filters[column.id] ?? ''}
                                    onChange={(event) => {
                                        setFilters((prev) => ({ ...prev, [column.id]: event.target.value }));
                                        setPage(0);
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            </Menu>

            <Menu anchorEl={orderAnchor} open={Boolean(orderAnchor)} onClose={() => setOrderAnchor(null)}>
                <Box sx={{ p: 1.5, width: 280 }}>
                    <Typography variant="subtitle2" sx={{ px: 1, mb: 1 }}>{t('hardcoded.components_common_advanceddatatable.orden_de_columnas')}</Typography>
                    {columnOrder.map((columnId, idx) => {
                        const column = columnsById[columnId];
                        if (!column) return null;
                        return (
                            <Box key={columnId} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>{column.label}</Typography>
                                <IconButton size="small" disabled={idx === 0} onClick={() => moveColumn(columnId, 'up')}>
                                    <ArrowUpwardIcon fontSize="inherit" />
                                </IconButton>
                                <IconButton size="small" disabled={idx === columnOrder.length - 1} onClick={() => moveColumn(columnId, 'down')}>
                                    <ArrowDownwardIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>
            </Menu>
        </Paper>
    );
};

export default AdvancedDataTable;
